import React, { FC, useContext } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';

// Components
import BaseContentContainer from '../../components/base-content-container/base-content-container';
import ButtonsBar from '../../components/buttons-bar/buttons-bar';
import SubmitButton from '../../components/submit-button/submit-button';

// Containers
import { AnnouncementFormInputs, AnnouncementRecipients, useAnnouncementForm } from './announcement-form';

// Helpers
import {
  getAnnouncementData,
  createSetModalContent,
  deleteAnnouncement,
  buildAnnouncementRequest,
} from '../../helpers/announcement-helper';
import { recipientsToStudentIds } from '../../helpers/student-uuid-helper';

// Models
import {
  BaseContentContainerActionButtonPosition,
  BaseContentContainerBiggerSide,
  BaseContentContainerFormat,
} from '../../models/base-content-container';

// Providers
import { GeneralContext } from '../../providers/general-provider';

// Services
import AnnouncementService from '../../services/announcement-service';
import FileStorageService from '../../services/file-storage-service';

const AnnouncementEdit: FC<{
  announcement: AnnouncementDto;
  classroomList: ClassroomStudents[];
  announcementService: AnnouncementService;
  fileStorageService: FileStorageService;
}> = ({ announcement, classroomList, announcementService, fileStorageService }) => {
  const navigate = useNavigate();
  const { setModalContent, setToastContent } = useContext(GeneralContext);
  const { announcementId } = useParams<{ announcementId: string }>();

  const form = useAnnouncementForm(() => {
    const initialData = getAnnouncementData(announcement);
    const { classroomIds, studentIds, unmatchedStudentUuids } = recipientsToStudentIds(
      announcement.recipients,
      classroomList
    );

    return {
      ...initialData,
      classData: classroomIds,
      studentData: studentIds,
      unmatchedStudentUuids,
    };
  });

  const save = (scheduleDate: string | null) =>
    form
      .submitOnce(async (current) => {
        await announcementService.updateAnnouncement(
          announcementId,
          buildAnnouncementRequest({ ...current, scheduleDate }, classroomList)
        );
        setToastContent({
          isOpen: true,
          text: scheduleDate ? 'Comunicado agendado!' : 'Comunicado enviado!',
          type: 'success',
        });
        navigate('/comunicados/listar');
      })
      .catch(() => {
        setToastContent({
          isOpen: true,
          text: 'Houve um erro ao salvar o comunicado.',
          type: 'error',
        });
      });

  return (
    <BaseContentContainer
      biggerSide={BaseContentContainerBiggerSide.right}
      format={BaseContentContainerFormat.doubleWithActionButtons}
      actionButtonPosition={BaseContentContainerActionButtonPosition.bottom}
    >
      <AnnouncementFormInputs
        form={form}
        announcement={announcement}
        fileStorageService={fileStorageService}
      />
      <AnnouncementRecipients form={form} classroomList={classroomList} />
      <ButtonsBar pattern='firstSeparate' verticalAlign='bottom'>
        <Link to='/comunicados/listar'>
          <SubmitButton value='cancelar' variant='terciary' />
        </Link>
        <SubmitButton
          value='deletar'
          variant='terciary'
          onClick={() =>
            deleteAnnouncement(
              announcementService,
              announcementId,
              setModalContent,
              setToastContent,
              navigate
            )
          }
        />
        <SubmitButton
          value='reagendar envio'
          variant='secondary'
          onClick={() =>
            createSetModalContent(
              form.data,
              (submitData) => save(submitData.scheduleDate),
              form.setData,
              setModalContent
            )
          }
          disabled={!form.allowSubmit}
        />
        <SubmitButton
          value='enviar'
          variant='primary'
          onClick={() => save(null)}
          disabled={!form.allowSubmit}
        />
      </ButtonsBar>
    </BaseContentContainer>
  );
};

export default AnnouncementEdit;
